import { DatePipe, DecimalPipe, Location } from "@angular/common";
import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { NgForm } from "@angular/forms";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { DateTime } from "luxon";
import {
  concat,
  Observable,
  of,
  Subject,
  Subscription,
  throwError,
} from "rxjs";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  flatMap,
  map,
  switchMap,
} from "rxjs/operators";
import { conformToMask } from "text-mask-core";
import { environment } from "../../../environments/environment";
import { TicketCutoffTypeEnum } from "../../enum/cutoff-type.enum";
import { GroupsEnum } from "../../enum/groups.enum";
import { MenuConstants } from "../../infrastructure/MenuConstants";
import { AcquiredFund } from "../../model/domain/AcquiredFund";
import { AcquiredFundAccount } from "../../model/domain/AcquiredFundAccount";
import { Bank } from "../../model/domain/Bank";
import {
  DialogSize,
  OperationTypeLiabilities,
  SettlementType,
  TicketStatus,
  TicketType,
  WorkflowStatus,
} from "../../model/domain/Entity";
import { LiabilitiesModel } from "../../model/domain/LiabilitiesModel";
import { ShareholderAccountModel } from "../../model/domain/ShareholderAccountModel";
import { ShareholderFundPositionFundInvoiceModel } from "../../model/domain/ShareholderFundPositionModel";
import { ShareholderModel } from "../../model/domain/ShareholderModel";
import { NamedType } from "../../model/domain/Tipo";
import { AuthService } from "../../services/auth.service";
import { BankService } from "../../services/bank.service";
import { CutoffService } from "../../services/cutoff.service";
import { DialogService } from "../../services/dialog.service";
import { DomainService } from "../../services/domain.service";
import { FileService } from "../../services/file.service";
import { LoaderService } from "../../services/loader.service";
import { SnackbarService } from "../../services/snackbar.service";
import { TicketService } from "../../services/ticket.service";
import { ValidatorService } from "../../services/validator.service";
import { AnnotationComponent } from "../annotation/annotation.component";
import { Roles } from "../auth/auth.role";
import { TicketCutoffPermission } from "../cutoff/ticket-cutoff-type.model";

@Component({
  selector: "app-liabilities",
  templateUrl: "./liabilities.component.html",
  styleUrls: ["./liabilities.component.css"],
})
export class LiabilitiesComponent implements OnInit, OnDestroy {
  faInfoCircle = faInfoCircle;
  public groups = GroupsEnum;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: TicketService,
    public validator: ValidatorService,
    private datePipe: DatePipe,
    private snackBarService: SnackbarService,
    private cutoffService: CutoffService,
    private authService: AuthService,
    private dialogService: DialogService,
    private location: Location,
    private loaderService: LoaderService,
    private domainService: DomainService,
    private fileService: FileService,
    private bankService: BankService
  ) {
    this.navigationSubscription = this.router.events.subscribe((e: any) => {
      if (e instanceof NavigationEnd) {
        this.resetForm();
      }
    });
  }

  @ViewChild("thisForm", { static: true }) public frm: NgForm;
  @ViewChild("annotation", { static: true })
  annotationComponent: AnnotationComponent;

  dataChanged: EventEmitter<string>;

  navigationSubscription: Subscription;

  OperationTypeLiabilities = OperationTypeLiabilities;
  TipoLiquidacao = SettlementType;
  TicketTypeEnum = TicketType;

  ListaTipoLiquidacao: NamedType<number>[];
  ListOperationTypesLiabilities: NamedType<number>[];
  ListSettlementTypes: NamedType<number>[];

  isEditing = false;
  canValidate = false;
  sovereignActive = false;
  acquiredFundDocumentIsDisabled = false;
  acquiredFundNameIsDisabled = false;
  manualsettlementDateIsDisabled: boolean = true;
  newFundIsDisabled = false;
  mnemonicCodeIsDisabled = false;
  quotationDateIsDisabled = true;

  model: LiabilitiesModel = null;
  banksList: Bank[] = null;
  banksSettlementList: string[] = null;
  acquiredFundAccountList: AcquiredFundAccount[] = null;
  selectedAcquiredFundAccount: AcquiredFundAccount = null;

  shareholders: ShareholderModel[] = null;
  shareholderAccountList: ShareholderAccountModel[] = null;
  selectedShareholderAccount: ShareholderAccountModel = null;

  numberMask: any;
  decimalPipe = new DecimalPipe("pt");

  // Invested Fund
  acquiredFundsByName: Observable<AcquiredFund[]>;
  acquiredFundsByDocument: Observable<AcquiredFund[]>;
  acquiredFundSearchNameTerm = new Subject<string>();
  acquiredFundSearchDocumentTerm = new Subject<string>();
  selectedAcquiredFund: AcquiredFund = new AcquiredFund();

  // Shareholder
  shareholdersByName: Observable<ShareholderModel[]>;
  shareholdersByDocument: Observable<ShareholderModel[]>;
  shareholderSearchNameTerm = new Subject<string>();
  shareholderSearchDocumentTerm = new Subject<string>();
  selectedShareholder: ShareholderModel = new ShareholderModel();

  // Custody Bank
  custodyBank: string = null;
  custodyBranch: string = null;
  custodyAccount: string = null;

  showEletronicTransferAction = false;
  isNotProduction = environment.name != 'production';

  // portfolioName: string = null;
  // portfolioDocument: string = null;
  // portfolioBank: string = null;
  // portfolioBranch: string = null;
  // portfolioAccount: string = null;
  // portfolioClearingAccount: string = null;

  // TODO: utilizar um validador já existente, talvez uma diretiva do próprio angular
  validators = {
    // tslint:disable-next-line:max-line-length
    operationTypeId: (value) =>
      [
        OperationTypeLiabilities.Application,
        OperationTypeLiabilities.Redemption,
        OperationTypeLiabilities.FullRedemption,
      ].includes(value),
    operationValue: (value) =>
      this.isOperationValueRequired()
        ? this.coalesce(value).length > 0 &&
          conformToMask(value, this.validator.currencyMaskDoubleDecimal, {})
        : true,
    quotaValue: (value) =>
      this.isQuotaValueRequired()
        ? this.coalesce(value).length > 0 &&
          conformToMask(value, this.validator.currencyMask, {})
        : this.coalesce(value).length === 0 ||
          conformToMask(value, this.validator.currencyMask, {}),
    settlementTypeId: (value) =>
      [SettlementType.CETIP, SettlementType.TED].includes(value),
    fundDocument: (value) =>
      this.model.isNewFund
        ? this.validator.validateDocument(value)
        : this.coalesce(value).length > 0,
    counterpartDocument: (value) =>
      this.coalesce(value).length === 0 ||
      this.validator.validateDocument(value),
    settlementDate: (value) =>
      !this.manualsettlementDateIsDisabled ?
        this.coalesce(value).length === 0 ||
        DateTime.fromFormat(value, "dd/MM/yyyy").isValid 
        : true,
    quotationDate: (value) =>
      this.coalesce(value).length === 0 ||
      DateTime.fromFormat(value, "dd/MM/yyyy").isValid,
    fundName: (value) => this.coalesce(value).length > 0,
    subscriptionDate: (value) =>
      this.coalesce(value).length === 0 ||
      DateTime.fromFormat(value, "dd/MM/yyyy").isValid,
    portfolioDocument: (value) =>
      this.model.isNewShareholder
        ? this.validator.validateCPFCNPJ(value)
        : this.coalesce(value).length > 0,
    portfolioName: (value) => this.coalesce(value).length > 0,
    portfolioBranch: (value) => this.coalesce(value).length > 0,
    portfolioAccount: (value) => this.coalesce(value).length > 0,
    portfolioBank: (value) => this.coalesce(value).length > 0,
  };

  ngOnInit() {
    this.dataChanged = new EventEmitter<string>();
    this.getBanksList();
    this.getListBanksSettlement();
    this.searchAcquiredFunds();
    this.searchShareholders();
    this.getTicket();
  }

  ngOnDestroy() {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  containsGroup(groups: GroupsEnum[]): boolean {
    return this.authService.hasGroup(groups);
  }

  resetForm() {
    this.model = new LiabilitiesModel();
    this.model.portfolioName = null;
    this.model.portfolioDocument = null;
    this.model.portfolioBank = null;
    this.model.portfolioBranch = null;
    this.model.portfolioAccount = null;
    this.selectedAcquiredFund = new AcquiredFund();
    this.acquiredFundAccountList = null;
    this.selectedAcquiredFundAccount = null;

    this.frm.reset();
    this.frm.resetForm();

    this.annotationComponent.annotation = null;

    this.isEditing = false;
    this.canValidate = false;
    this.model.id = 0;
    this.model.command = "0";
    this.model.isFIDC = false;
    this.model.isNewFund = false;
    this.model.isNewCounterpart = false;
    this.model.isSecondaryMarket = null;

    this.model.operationDate = new Date();
    this.dataChanged = new EventEmitter<string>();
    this.setEnable(true);
  }

  setEnable(enabled: boolean = false) {
    setTimeout(() => {
      if (enabled) {
        Object.values(this.frm.controls).forEach((c) => {
          c.enable();
        });
        this.enableSelect();
      } else if (!this.canEdit()) {
        this.disableSelect();
        this.annotationComponent.annotationIsDisabled = true;
        Object.values(this.frm.controls).forEach((c) => {
          c.disable();
        });
      }
    }, 10);
  }

  disableSelect() {
    this.acquiredFundDocumentIsDisabled = true;
    this.acquiredFundNameIsDisabled = true;
    this.manualsettlementDateIsDisabled =
      this.containsGroup([
        this.groups.AdmFiduci || this.groups.MiddleAdmFiduci,
      ]) && this.model.statusId != WorkflowStatus.PendingAdmApproval;
  }

  enableSelect() {
    this.manualsettlementDateIsDisabled =
      this.containsGroup([
        this.groups.AdmFiduci || this.groups.MiddleAdmFiduci,
      ]) && this.model.statusId != WorkflowStatus.PendingAdmApproval;
  }

  getTicket(idBoleta: number = null): void {
    this.model = new LiabilitiesModel();
    const id =
      idBoleta === null ? +this.route.snapshot.paramMap.get("id") : idBoleta;
    this.model.command = "0";
    this.model.id = id;

    this.ListSettlementTypes = this.service.ListSettlementTypesLiabilities();

    this.ListOperationTypesLiabilities =
      this.service.ListOperationTypesLiabilities();

    if (id === 0) {
      this.model.settlementTypeId = this.ListSettlementTypes[0].id;
      this.model.operationTypeId = this.ListOperationTypesLiabilities[0].id;
    }

    if (id) {
      this.service.Liabilities.getTicket(id).subscribe(
        (q) => {
          Object.assign(this.model, q);

          this.model.portfolioDocument = this.validator.formatDocument(
            this.model.portfolioDocument
          );

          if (this.model.isNewFund) {
            this.model.fundDocument = this.validator.formatDocument(
              this.model.fundDocument
            );
          } else {
            this.selectedAcquiredFund.document = this.validator.formatDocument(
              this.model.fundDocument
            );
            this.selectedAcquiredFund.name = this.model.fundName;
            this.selectedAcquiredFund.administrator =
              this.model.fundAdministrator;
          }

          if (
            this.model.isManualSettlementDate &&
            this.model.statusId == WorkflowStatus.PendingAdmApproval
          ) {
            this.model.settlementDate = null;
          }

          if (this.model.counterpartDocument) {
            this.model.counterpartDocument = this.validator.formatDocument(
              this.model.counterpartDocument
            );
          }

          this.model.operationValue = this.decimalPipe.transform(
            this.model.operationValue,
            "1.0-2",
            "pt-BR"
          ) as any;

          if (this.model.amount) {
            this.model.amount = this.decimalPipe.transform(
              this.model.amount,
              "1.0-8",
              "pt-BR"
            ) as any;
          }

          if (this.model.quotaValue) {
            this.model.quotaValue = this.decimalPipe.transform(
              this.model.quotaValue,
              "1.0-8",
              "pt-BR"
            ) as any;
          }

          if (
            this.model.fundDocument ===
              this.validator.sanitize(this.model.counterpartDocument) &&
            this.model.fundName === this.model.counterpartName
          ) {
            let acquiredFundAccount = {} as AcquiredFundAccount;
            acquiredFundAccount.bank = this.model.fundBank;
            acquiredFundAccount.branch = this.model.fundBranch;
            acquiredFundAccount.account = this.model.fundAccount;

            this.acquiredFundAccountList = [];
            this.acquiredFundAccountList.push(acquiredFundAccount);
            this.selectedAcquiredFundAccount = this.acquiredFundAccountList[0];
          }

          this.fillComponentFieldsLoad(this.model);

          this.model.settlementDate = this.datePipe.transform(
            this.model.settlementDate,
            "dd/MM/yyyy"
          ) as any;
          this.model.quotationDate = this.datePipe.transform(
            this.model.quotationDate,
            "dd/MM/yyyy"
          ) as any;
          this.model.subscriptionDate = this.datePipe.transform(
            this.model.subscriptionDate,
            "dd/MM/yyyy"
          ) as any;

          this.isEditing = true;
          this.canValidate = true;

          this.dataChanged.emit("");

          this.setEnable(false);
        },
        () => this.router.navigate([MenuConstants.Tickets.Liabilities])
      );
    } else {
      if (!this.model.statusId) {
        this.model.operationDate = new Date();
      }
    }
  }

  public fillComponentFieldsLoad<T>(model: any) {
    let data: any;
    data = Object.assign({}, model);
    try {
      this.selectedShareholder.document = data.portfolioDocument;
      this.selectedShareholder.name = data.portfolioName;
      // this.portfolioFundId = this.selectedShareholder.id = data.portfolioFundId;

      this.selectedShareholderAccount = new ShareholderAccountModel();
      this.selectedShareholderAccount.bank = data.portfolioBank;
      this.selectedShareholderAccount.branch = data.portfolioBranch;
      this.selectedShareholderAccount.account = data.portfolioAccount;
      // this.portfolio.fundAccountId = this.selectedShareholderAccount.id = data.portfolioFundAccountId;
      this.bankService.sendBank(this.selectedShareholderAccount.bank);

      if (this.isCETIP()) {
        this.selectedShareholderAccount.cetipAccount =
          data.portfolioCetipAccount;
      }
    } catch (error) {
      return throwError(error);
    }
  }

  getBanksList() {
    this.domainService.ListBanks().subscribe((b) => {
      this.banksList = b;
    });
  }

  getListBanksSettlement() {
    this.service.getListBanksSettlement().subscribe((b) => {
      this.banksSettlementList = b;
    });
  }

  searchAcquiredFunds() {
    this.acquiredFundsByName = concat(
      of([]),
      this.acquiredFundSearchNameTerm.pipe(
        filter((term) => term?.length >= 3),
        debounceTime(200),
        distinctUntilChanged(),
        switchMap((name) =>
          this.service
            .AcquiredFunds(name, null, null)
            .pipe(catchError(() => of([])))
        )
      )
    );

    this.acquiredFundsByDocument = concat(
      of([]),
      this.acquiredFundSearchDocumentTerm.pipe(
        filter((term) => term?.length >= 3),
        debounceTime(200),
        distinctUntilChanged(),
        switchMap((document) =>
          this.service
            .AcquiredFunds(null, document, null)
            .pipe(catchError(() => of([])))
        )
      )
    );
  }

  searchShareholders() {
    this.shareholdersByName = concat(
      of([]),
      this.shareholderSearchNameTerm.pipe(
        filter((term) => term?.length >= 3),
        debounceTime(200),
        distinctUntilChanged(),
        switchMap((name) =>
          this.service
            .getShareholders(name, null)
            .pipe(catchError(() => of([])))
        )
      )
    );

    this.shareholdersByDocument = concat(
      of([]),
      this.shareholderSearchDocumentTerm.pipe(
        filter((term) => term?.length >= 3),
        debounceTime(200),
        distinctUntilChanged(),
        switchMap((document) =>
          this.service
            .getShareholders(null, document)
            .pipe(catchError(() => of([])))
        )
      )
    );
  }

  fillAcquiredFundSelected(acquiredFund: AcquiredFund = null) {
    if (acquiredFund) {
      this.selectedAcquiredFund = acquiredFund;
    }

    this.model.fundName = "";
    this.model.fundAdministrator = "";
    this.model.fundDocument = "";
    this.model.fundBank = "";
    this.model.fundBranch = "";
    this.model.fundAccount = "";
    this.model.counterpartName = "";
    this.model.counterpartDocument = "";
    this.model.acquiredFundSourceId = "";

    this.selectedAcquiredFundAccount = null;
    this.acquiredFundAccountList = null;

    if (this.selectedAcquiredFund && this.selectedAcquiredFund.document) {
      const fundDocument = this.validator.sanitize(
        this.selectedAcquiredFund.document
      );
      this.model.fundDocument = this.selectedAcquiredFund.document;
      this.service.Liabilities.getQuotationDate(
        fundDocument,
        this.model.operationTypeId
      ).subscribe((q) => {
        this.model.quotationDate = this.datePipe.transform(
          q,
          "dd/MM/yyyy"
        ) as any;
        const quotationDate = new Date(
          q.toString().replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
        );
        this.service.Liabilities.getSettlementDate(
          fundDocument,
          this.model.operationTypeId,
          quotationDate
        ).subscribe((s) => {
          this.model.settlementDate = this.datePipe.transform(
            s,
            "dd/MM/yyyy"
          ) as any;
        });
      });

      this.model.acquiredFundSourceId = this.selectedAcquiredFund.sourceId;
      this.model.fundName = this.selectedAcquiredFund.name;
      this.model.fundAdministrator = this.selectedAcquiredFund.administrator;
      this.model.fundDocument = this.selectedAcquiredFund.document =
        this.validator.formatDocument(this.selectedAcquiredFund.document);
      this.model.isManualSettlementDate =
        this.selectedAcquiredFund.isManualSettlementDate;
      if (this.selectedAcquiredFund) {
        this.service
          .getAcquiredFundAccounts(this.selectedAcquiredFund.sourceId)
          .subscribe((res) => {
            this.acquiredFundAccountList = res;

            if (this.acquiredFundAccountList.length === 1) {
              this.model.fundBank = this.acquiredFundAccountList[0].bank;
              this.model.fundBranch = this.acquiredFundAccountList[0].branch;
              this.model.fundAccount = this.acquiredFundAccountList[0].account;
              this.selectedAcquiredFundAccount =
                this.acquiredFundAccountList[0];
              this.model.counterpartDocument =
                this.selectedAcquiredFund.document;
              this.model.counterpartName = this.selectedAcquiredFund.name;
            }
          });
      }
    }
  }

  fillShareholderSelected(shareholder: ShareholderModel = null) {
    if (shareholder) {
      this.selectedShareholder = shareholder;
    }

    this.model.portfolioName = "";
    this.model.portfolioDocument = "";
    this.model.portfolioBank = "";
    this.model.portfolioBranch = "";
    this.model.portfolioAccount = "";
    this.model.portfolioCetipAccount = "";
    this.model.portfolioCode = "";

    this.selectedShareholderAccount = null;
    this.shareholderAccountList = null;

    if (this.selectedShareholder && this.selectedShareholder.document) {
      this.model.portfolioName = this.selectedShareholder.name;
      this.model.portfolioDocument = this.selectedShareholder.document =
        this.validator.formatDocument(this.selectedShareholder.document);

      if (this.selectedShareholder.cetipAccount) {
        this.model.portfolioCetipAccount =
          this.selectedShareholder.cetipAccount;
      }

      this.model.portfolioCode = this.selectedShareholder.sourceId;

      this.service
        .getShareholderAccounts(this.selectedShareholder.sourceId)
        .subscribe((res) => {
          this.shareholderAccountList = res;

          if (this.shareholderAccountList != null) {
            this.model.shareholderHasAccount =
              this.shareholderAccountList.length > 0;

            if (this.shareholderAccountList.length === 1) {
              this.model.portfolioBank = this.shareholderAccountList[0].bank;
              this.model.portfolioBranch =
                this.shareholderAccountList[0].branch;
              this.model.portfolioAccount =
                this.shareholderAccountList[0].account;

              this.selectedShareholderAccount = this.shareholderAccountList[0];
            }
          }
        });
    }
  }

  submit(): void {
    this.loaderService.addLoader();
    const editing = this.isEditing;

    this.register(this.frm)
      .pipe(
        catchError((err) => {
          this.loaderService.removeLoader();

          this.snackBarService.open(err, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });
          return throwError(() => err);
        })
      )
      .subscribe(
        (r) => {
          this.loaderService.removeLoader();

          const msg = editing ? "Boleta alterada." : "Boleta cadastrada.";

          this.snackBarService.open(msg, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });
        },
        () => {}
      );
  }

  register(
    f: NgForm,
    duplicate: boolean = false
  ): Observable<LiabilitiesModel> {
    const editing = this.isEditing;

    if (!this.canEdit() && !duplicate) {
      return;
    }

    Object.values(f.controls).forEach((c) => {
      c.markAsTouched();
      c.updateValueAndValidity();
    });

    this.canValidate = true;

    const errors = this.getErrors();

    if (f.invalid || errors.length > 0) {
      return throwError(() => "Verifique os campos destacados.");
    }

    if (this.acquiredFundAccountList != null && this.acquiredFundAccountList.length == 0) {
      return throwError(() => "O fundo investido não está cadastrado.");
    }

    if (this.model.isSecondaryMarket) {
      var hasAttachment = false;

      var errorText =
        "Para cotas negociadas em Balcão, é necessário apresentar cópia dos comprovantes e contrato de negociação. Se houve ganho de capital, apresentar, também, o documento de recolhimento dos tributos, quando exigido por lei.";
      if (this.model.id == 0 && this.model.tempTicketId == null) {
        return throwError(() => errorText);
      } else {
        this.fileService
          .getFilesPaginatedAttachment(
            this.model.tempTicketId,
            this.model.id,
            1,
            10
          )
          .subscribe((files) => {
            hasAttachment = files != null && files.rowCount > 0;

            if (!hasAttachment) {
              return throwError(() => errorText);
            }
          });
      }
    }

    let data = Object.assign({}, this.model);

    if (data.settlementTypeId === SettlementType.CETIP) {
      data.isCetipVoice = data.isCetipVoice || false;
      data.hasSameOwnership = false;

      data.fundBank = null;
      data.fundBranch = null;
      data.fundAccount = null;

      if (data.counterpartDocument !== null) {
        data.counterpartDocument = this.validator.sanitize(
          data.counterpartDocument
        );
      }
    }

    if (this.isCETIPVoiceFixed()) {
      data.isCetipVoice = true;
    } else {
      data.isCetipVoice = false;
    }

    if (this.isTED()) {
      data.hasSameOwnership = data.hasSameOwnership || false;
      data.isCetipVoice = false;

      if (!this.hasCounterpart()) {
        data.counterpartName = null;
        data.counterpartDocument = null;
      } else {
        if (data.counterpartDocument !== null) {
          data.counterpartDocument = this.validator.sanitize(
            data.counterpartDocument
          );
        }
      }
      data.counterpartCetipAccount = null;
      data.command = "0";
      data.portfolioCetipAccount = null;
    }

    if (!data.isNewFund) {
      data.issuerName = null;
    }

    if (!data.isCetipVoice) {
      data.cetipVoiceId = null;
    }

    if (!this.isNoFinancial() && data.isSecondaryMarket === null) {
      data.isSecondaryMarket = false;
    }

    data.operationDate = null;
    data.statusId = null;
    data.id = null;

    data.amount = this.validator.getCurrency(data.amount);
    data.quotaValue = this.validator.getCurrency(data.quotaValue);
    data.operationValue = this.validator.getCurrency(data.operationValue);

    data.portfolioName = this.model.portfolioName;
    data.portfolioDocument = this.validator.sanitize(
      this.model.portfolioDocument
    );
    if (this.model.portfolioCetipAccount != null && this.isCETIP()) {
      data.portfolioCetipAccount = this.model.portfolioCetipAccount;
    }
    data.portfolioBank = this.model.portfolioBank;
    data.portfolioBranch = this.model.portfolioBranch;
    data.portfolioAccount = this.model.portfolioAccount;

    if (duplicate) {
      data.workflowStartDate = null;
      data.workflowEndDate = null;
    }

    if (data.settlementDate != null) {
      data.settlementDate = new Date(
        data.settlementDate
          .toString()
          .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
    }

    if (data.quotationDate != null) {
      data.quotationDate = new Date(
        data.quotationDate
          .toString()
          .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
    }

    if (data.subscriptionDate != null) {
      data.subscriptionDate = new Date(
        data.subscriptionDate
          .toString()
          .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
    }

    if (
      this.model.isNewFund ||
      this.model.operationTypeId !== OperationTypeLiabilities.Application
    ) {
      data.fundDocument = this.validator.sanitize(data.fundDocument);

      if (data.counterpartDocument) {
        data.counterpartDocument = this.validator.sanitize(
          data.counterpartDocument
        );
      }
    } else {
      data.fundDocument = this.validator.sanitize(
        this.selectedAcquiredFund.document
      );
      data.fundName = this.selectedAcquiredFund.name;
    }

    data.annotations = this.annotationComponent.annotation;

    if (data.isNewFund) data.isManualSettlementDate = false;

    const method =
      editing && !duplicate
        ? this.service.Liabilities.updateTicket(this.model.id, data)
        : this.service.Liabilities.registerTicket(data);

    return method
      .pipe(
        catchError((err) => {
          return throwError("Erro ao cadastrar a boleta.");
        })
      )
      .pipe(
        map((fund: LiabilitiesModel) => {
          if (!editing) {
            this.model.id = fund.id;
            this.model.operationDate = fund.operationDate;
            this.model.statusId = fund.statusId;
            this.model.accountManagerName = fund.accountManagerName;
            this.model.accountManagerDocument = fund.accountManagerDocument;
            this.model.accountManagerId = fund.accountManagerId;
            this.model.portfolioFundId = fund.portfolioFundId;
            this.model.settlementDate = this.datePipe.transform(
              fund.settlementDate,
              "dd/MM/yyyy"
            ) as any;
            this.model.quotationDate = this.datePipe.transform(
              fund.quotationDate,
              "dd/MM/yyyy"
            ) as any;
            this.model.subscriptionDate = this.datePipe.transform(
              fund.subscriptionDate,
              "dd/MM/yyyy"
            ) as any;
            this.model.ticketCutoffTypeId = fund.ticketCutoffTypeId;

            this.isEditing = true;

            const url = this.router.createUrlTree([this.model.id], {
              relativeTo: this.route,
            });
            this.location.go(url.toString());
          }

          return this.model;
        })
      );
  }

  confirmForward(): Promise<boolean> {
    if (!this.canEdit()) {
      return Promise.resolve(false);
    }

    var textConfirm = 'A boleta não poderá mais ser editada, deseja continuar?';

    if(this.isNotProduction) {
      textConfirm += '<br />ATENÇÃO: ESTE É UM AMBIENTE DE TESTE. NÃO EXECUTE OPERAÇÕES REAIS.';
    }

    return this.dialogService
      .confirm(
        "Encaminhar Boleta",
        textConfirm
      )
      .toPromise()
      .then((result) => {
        if (!result) {
          return false;
        }

        const mensagem =
          "É necessário enviar a ordem desta operação para a Contraparte (Fundo Investido), \
      com a confirmação que será convertida, para que seja processada corretamente.<br /><br />\
      O não atendimento do procedimento acima poderá acarretar em cancelamento da operação e/ou atraso na \
      divulgação da cota do Fundo.";

        return this.dialogService
          .confirm("Alerta", mensagem, "OK", "Cancelar", DialogSize.Medium)
          .toPromise()
          .then((dialogResult) => {
            if (!dialogResult) {
              return false;
            }
            const operationDate = DateTime.fromJSDate(
              new Date(this.model.operationDate.toString())
            );

            if (operationDate.day < DateTime.local().day) {
              return this.dialogService
                .confirm(
                  "Boleta Retroativa",
                  "A data de operação da boleta será alterada para a data de hoje.",
                  "OK",
                  "Cancelar"
                )
                .toPromise();
            }

            return true;
          });
      });
  }

  async forward(): Promise<void> {
    const cutoffPermission = await this.checkPermission();

    if (!cutoffPermission.isAllowed) {
      this.snackBarService.open(
        this.cutoffService.warningMessage +
          this.cutoffService.dateTimeOffsetToTimeString(
            cutoffPermission.timeLimit
          ),
        "OK"
      );
      return;
    }

    this.confirmForward().then((ok) => {
      if (!ok) {
        return;
      }
      this.register(this.frm)
        .pipe(
          catchError((err) => {
            this.snackBarService.open(err, "OK", {
              verticalPosition: "top",
              horizontalPosition: "right",
            });
            return throwError(err);
          })
        )
        .subscribe(
          (fund: LiabilitiesModel) => {
            this.service.Liabilities.forwardTicket(fund.id).subscribe((res) => {
              this.model.statusDescription = res.statusDescription;

              Object.values(this.frm.controls).forEach((c) => {
                c.disable();
              });

              this.snackBarService.open("Boleta encaminhada.", "OK", {
                verticalPosition: "top",
                horizontalPosition: "right",
              });

              this.getTicket(fund.id);
            });
          },
          () => {}
        );
    });
  }

  cancel() {
    return this.dialogService
      .confirm("Cancelar Boleta", "Deseja cancelar esta boleta?")
      .pipe(
        flatMap((ok) => {
          if (ok) {
            return this.service.Liabilities.cancelDraftTicket(this.model.id);
          }

          return of(null);
        })
      )
      .subscribe((res) => {
        if (res) {
          this.model.statusDescription = res.statusDescription;

          Object.values(this.frm.controls).forEach((c) => {
            c.disable();
          });

          this.snackBarService.open("Boleta cancelada.", "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });

          this.getTicket(this.model.id);
        }
      });
  }

  approve(): void {
    this.dialogService
      .confirm("Aprovar Boleta", "Deseja aprovar esta boleta?")
      .subscribe((result) => {
        if (result) {
          this.ticketUpdateAdmFiduci();
          this.ticketApprove();
        }
      });
  }

  private ticketApprove() {
    this.service.approve(this.model.id, "").subscribe(() => {
      this.snackBarService.open("Boleta atualizada com sucesso.");

      this.getTicket(this.model.id);
    });
  }

  private ticketUpdateAdmFiduci() {
    if (
      this.model.isManualSettlementDate &&
      this.containsGroup([this.groups.AdmFiduci || this.groups.MiddleAdmFiduci])
    ) {
      this.model.settlementDate = new Date(
        this.model.settlementDate
          .toString()
          .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
      this.service.Liabilities.updateSettlementDate(
        this.model.id,
        this.model.settlementDate
      );
    }
  }

  reject(): void {
    this.dialogService
      .reject("Reprovar Boleta", "Deseja reprovar esta boleta?")
      .subscribe((result) => {
        if (result) {
          this.ticketCancel(result.justification);
        }
      });
  }

  private ticketCancel(justification: string) {
    this.service.reject(this.model.id, justification).subscribe(() => {
      this.snackBarService.open("Boleta atualizada com sucesso.");
      this.getTicket(this.model.id);
    });
  }

  duplicate() {
    this.register(this.frm, true)
      .pipe(
        catchError((err) => {
          this.loaderService.removeLoader();

          this.snackBarService.open(err, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });
          return throwError(err);
        })
      )
      .subscribe((newTicket) => {
        this.snackBarService
          .open("Boleta duplicada.", "Visualizar no Monitor", {
            verticalPosition: "top",
            horizontalPosition: "right",
            duration: 6000,
          })
          .onAction()
          .subscribe((a) => {
            this.router.navigate([
              MenuConstants.Todos,
              { ticketId: TicketType.Liabilities },
            ]);
          });
      });
  }

  coalesce(value: any) {
    return value || "";
  }

  hasError(prop: string): boolean {
    if (!this.canValidate) {
      return false;
    }

    return !this.validators[prop](this.model[prop]);
  }

  getErrors(): string[] {
    const validationErrors = [];

    Object.keys(this.validators).forEach((k) => {
      if (!this.validators[k](this.model[k])) {
        validationErrors.push(k);
      }
    });

    return validationErrors;
  }

  isRedemption(): boolean {
    return this.model.operationTypeId === OperationTypeLiabilities.Redemption;
  }

  isApplication(): boolean {
    return this.model.operationTypeId === OperationTypeLiabilities.Application;
  }

  isCETIP(): boolean {
    return this.model.settlementTypeId === SettlementType.CETIP;
  }

  isNoFinancial(): boolean {
    return this.model.settlementTypeId === SettlementType.NoFinancial;
  }

  isRescue(): boolean {
    return (
      this.model.operationTypeId === OperationTypeLiabilities.FullRedemption
    );
  }

  hasCounterpart(): boolean {
    return (
      this.isCETIP() ||
      (this.isTED() && !this.model.hasSameOwnership) ||
      this.isNoFinancial()
    );
  }

  isCETIPVoiceFixed(): boolean {
    return false; //this.model.isSecondaryMarket && this.isCETIP() && (this.model.fundType === FundType.CFF);
  }

  isTED(): boolean {
    return (
      this.model.settlementTypeId === SettlementType.TED ||
      this.model.settlementTypeId === SettlementType.Sovereign
    );
  }

  isOperationValueRequired() {
    const isRequired =
      (this.model.amount === null ||
        this.model.amount === 0 ||
        this.model.amount.toString() == "") &&
      (this.model.operationValue === null ||
        this.model.operationValue == 0 ||
        this.model.operationValue.toString() == "") &&
      this.model.settlementTypeId !== SettlementType.NoFinancial;

    return isRequired;
  }

  isQuotaValueRequired(): boolean {
    return false;
  }

  isAmountRequired() {
    const isRequired =
      (this.model.amount === null ||
        this.model.amount === 0 ||
        this.model.amount.toString() == "") &&
      (this.model.operationValue === null ||
        this.model.operationValue == 0 ||
        this.model.operationValue.toString() === "") &&
      this.model.settlementTypeId !== SettlementType.NoFinancial;

    return isRequired;
  }

  canEdit(): boolean {
    return (
      !this.model.statusId &&
      !this.model.statusDescription &&
      this.authService.hasRole(Roles.CreateLiabilitiesTicket)
    );
  }

  async checkPermission(): Promise<TicketCutoffPermission> {
    let cutoffTypeId = this.model.ticketCutoffTypeId;
    let accountManagerId = this.model.accountManagerId;
    let fundId = this.model.portfolioFundId;

    if (accountManagerId === null) {
      switch (this.model.settlementTypeId) {
        case SettlementType.CETIP:
          cutoffTypeId = TicketCutoffTypeEnum.LiabilitiesCetip;
          break;
        case SettlementType.TED:
          cutoffTypeId = TicketCutoffTypeEnum.LiabilitiesTed;
          break;
      }

      accountManagerId = this.authService.authToken.accountManagerId;
    }

    return this.cutoffService.isAvailable(
      cutoffTypeId,
      this.model.id,
      accountManagerId,
      fundId
    );
  }

  canDuplicate(): boolean {
    const _operationDate = this.datePipe.transform(
      this.model.operationDate,
      "yyyy-MM-dd"
    );
    const _duplicateTicketDateLimit = this.datePipe.transform(
      new Date(environment.duplicateTicketDateLimit),
      "yyyy-MM-dd"
    );

    return (
      this.model.id &&
      this.authService.hasRole(Roles.CreateLiabilitiesTicket) &&
      !(_operationDate <= _duplicateTicketDateLimit)
    );
  }

  canChangeStatus(): boolean {
    return (
      this.model.statusId &&
      this.authService.hasRoles(this.model.statusRequiredRoles)
    );
  }

  canCancel() {
    return (
      this.model.id &&
      (!this.model.statusId || this.model.statusId === TicketStatus.Draft) &&
      this.authService.hasRole(Roles.CreateLiabilitiesTicket)
    );
  }

  cleanAcquiredFunds() {
    this.model.fundName = null;
    this.model.fundAdministrator = null;
    this.model.fundDocument = null;
    this.model.quotationDate = null;
    this.model.settlementDate = null;
    this.model.subscriptionDate = null;
    this.model.fundBank = null;
    this.model.fundBranch = null;
    this.model.fundAccount = null;
    this.acquiredFundAccountList = null;

    if (
      this.model.settlementTypeId &&
      this.model.settlementTypeId !== SettlementType.Sovereign
    ) {
      this.selectedAcquiredFund = new AcquiredFund();
      this.acquiredFundDocumentIsDisabled = false;
      this.acquiredFundNameIsDisabled = false;
      this.newFundIsDisabled = false;
      this.mnemonicCodeIsDisabled = false;
    }

    if (!this.isEditing) {
      this.model.issuerName = null;
      this.model.isFIDC = false;
      this.model.quotationDate = null;
      this.model.settlementDate = null;
      this.model.subscriptionDate = null;
    }

    if (this.model.isNewFund) {
      this.quotationDateIsDisabled = false;
      this.manualsettlementDateIsDisabled = false;
    } else {
      this.quotationDateIsDisabled = true;
      this.manualsettlementDateIsDisabled = true;
    }
  }

  isOutCustody(): boolean {
    if (
      this.model.portfolioBank === null ||
      this.banksSettlementList === null ||
      this.banksSettlementList.includes(this.model.portfolioBank)
    ) {
      return false;
    } else {
      return true;
    }
  }

  /**
   * @description Check if can send tranfer for liquidation
   * @returns true if workflow status is awaiting Shipping / Receiving of Matera Transfer
   */
  canSendTransferForLiquidation(): boolean {
    return (
      this.model.statusId === 10 &&
      this.authService.hasRole(Roles.ApprovePendingSettlementCustody)
    );
  }

  /**
   * @description Check if can check status of Matera Tranfer
   * @returns true if workflow status is on transfer liquidation
   */
  canCheckStatusTransfer(): boolean {
    return (
      this.model.statusId === 11 &&
      this.authService.hasRole(Roles.ApproveActiveSettlement)
    );
  }

  isFundClosed(): boolean {
    return this.model.scheme === "1";
  }

  /**
   * @description Transfer ETA - Electronic Transfer Available (TED - Transferência Eletrônica Disponível) to Matera API
   */
  electronicTransferAvailable(): void {}

  checkEletronicTransferAction(): void {
    this.service.checkEletronicTransferTicket(this.model.id).subscribe(
      () => {
        this.showEletronicTransferAction = true;
      },
      () => {
        this.showEletronicTransferAction = false;
      }
    );
  }

  checkEletronicTransferSituation(): void {
    this.service.getEletronicTransferSituationTicket(this.model.id).subscribe(
      (result) => {
        this.snackBarService.open(`Status: ${result.status.description}`);
      },
      (err) => {
        this.snackBarService.open(`Erro: ${err.error.message}`);
      }
    );
  }

  setTempId(tempId) {
    this.model.tempTicketId = tempId;
  }

  setTempSettlementDate(settlementDate: Date) {
    this.model.settlementDate = settlementDate;
  }

  fillComponentWhenAcquiredFundAccountSelected() {
    this.model.fundBank = null;
    this.model.fundBranch = null;
    this.model.fundAccount = null;
    this.model.counterpartDocument = null;
    this.model.counterpartName = null;
    this.model.isManualSettlementDate = null;

    if (
      (this.model.isSecondaryMarket && !this.isNoFinancial()) ||
      (this.isTED() && this.acquiredFundAccountList.length > 0)
    ) {
      this.model.fundBank = this.selectedAcquiredFundAccount.bank;
      this.model.fundBranch = this.selectedAcquiredFundAccount.branch;
      this.model.fundAccount = this.selectedAcquiredFundAccount.account;
      this.model.counterpartDocument = this.selectedAcquiredFund.document;
      this.model.counterpartName = this.selectedAcquiredFund.name;
    }
  }

  fillComponentWhenShareholderAccountSelected() {
    this.model.portfolioBank = null;
    this.model.portfolioBranch = null;
    this.model.portfolioAccount = null;

    this.model.portfolioBank = this.selectedShareholderAccount.bank;
    this.model.portfolioBranch = this.selectedShareholderAccount.branch;
    this.model.portfolioAccount = this.selectedShareholderAccount.account;
  }

  cleanOrFillCounterpart() {
    if (this.model.isNewCounterpart) {
      this.model.fundBank = null;
      this.model.fundBranch = null;
      this.model.fundAccount = null;
      this.model.counterpartDocument = null;
      this.model.counterpartName = null;
    } else {
      this.fillComponentWhenAcquiredFundAccountSelected();
    }
  }

  counterpartDisable() {
    if (
      this.acquiredFundAccountList !== null &&
      this.acquiredFundAccountList.length > 0 &&
      !this.model.isNewCounterpart &&
      this.model.settlementTypeId === SettlementType.TED
    ) {
      return true;
    } else {
      return false;
    }
  }

  openStockFundsDialog() {
    this.dialogService
      .stockFundsDialog(
        this.selectedShareholder.sourceId,
        TicketType.Liabilities
      )
      .subscribe((res) => {
        this.getStockSelectedFund(res);
      });
  }

  viewStockButton(): boolean {
    return (
      this.model.operationTypeId === OperationTypeLiabilities.FullRedemption ||
      this.model.operationTypeId === OperationTypeLiabilities.Redemption
    );
  }

  getStockSelectedFund(selectedFund: ShareholderFundPositionFundInvoiceModel) {
    if (selectedFund) {
      this.model.operationValue = this.decimalPipe.transform(
        selectedFund.redemptionValue,
        "1.0-2",
        "pt-BR"
      ) as any;

      this.service
        .AcquiredFunds(null, null, selectedFund.fundSourceId)
        .subscribe((res) => {
          this.fillAcquiredFundSelected(res[0]);
        });
    }
  }
}
